import { isAthlete, isCoach } from '@/helpers/rolesHelper';
import { isPaddlingSession, isRowingSession, isWaterSession, isScRelatedSession } from '@/helpers/sessionHelper';
import get from 'get-object-path';
import { DateTime } from 'luxon';
const TODAY = DateTime.now().toISO();

export const editSession = {
    namespaced: true,

    state: {
        session: null,
        showDefaultComponent: false,
        setFromAnotherSessionId: null
    },

    getters: {
        clubCoaches(state, getters, rootState, rootGetters) {
            return rootGetters['clubHelper/clubCoaches'];
        },

        clubAthletes(context, getters, rootState, rootGetters) {
            return rootGetters['clubHelper/clubAthletes'];
        },

        getShowDefaultComponent(state) {
            return state.showDefaultComponent;
        },

        setFromAnotherSessionId(state) {
            return state.setFromAnotherSessionId;
        }
    },
    mutations: {
        updateSession(context, { session, details }) {
            details.name = session.name;
            details.sessionType = session.sport.id;
            details.all_day_event = session.all_day_event;
            details.attendance_limit = session.attendance_limit;
            details.seat_racing = session.seat_racing;
            details.session_id = session.session_id;
            details.favorite = session.favorite;
            details.planned_rpe = session.planned_rpe;

            details.startDate = DateTime.fromSQL(session.start_date).toISO();
            details.startTime = DateTime.fromSQL(session.start_date).toISO();
            details.endDate = DateTime.fromSQL(session.end_date).toISO();
            details.endTime = DateTime.fromSQL(session.end_date).toISO();
            details.previousStartDate = undefined;

            details.totalDistance = session.planned_distance || '';
            details.totalTime = session.planned_time || '';
            details.target_stroke = session.target_stroke || null;
            details.rolling_start = !!session.rolling_start;
            details.start_sequence = !!session.start_sequence;

            details.repeat.editing = true;
            details.repeat.repeat_frequency_type = session.repeat_frequency_type;
            details.repeat.repeat_every = session.repeat_every || 1;
            details.repeat.repeat_on_the_specific_step = session.repeat_on_the_specific_step || null;
            details.repeat.repeat_on_the_specific_every = session.repeat_on_the_specific_every || null;
            details.repeat.repeat_end_date =
                session.repeat_frequency_type === 'specific_dates'
                    ? null
                    : DateTime.fromSQL(session.repeat_end_date).toISO() || TODAY;
            const repeatNumbers = get(session, 'repeat_numbers.data') || [];
            details.repeat.repeat_numbers = repeatNumbers ? repeatNumbers.map(({ id }) => id) : null;
            details.repeat.checked = !!session.repeat_frequency_type;
            details.repeat.repeat_on_the_specific_days = session.repeat_on_the_specific_days || [];

            details.location = {
                id: get(session, 'venue.data.id'),
                formatted_address: get(session, 'venue.data.formatted_address'),
                name_address: get(session, 'venue.data.name'),
                lat: get(session, 'venue.data.latitude'),
                lng: get(session, 'venue.data.longitude')
            };
            details.description = session.description || '';
            details.weather = session.weather || '';

            const intensities = get(session, 'intensities.data') || [];
            intensities.forEach(item => {
                const intensity = details.intensities.find(({ id }) => id === item.id);
                if (!intensity) return;
                intensity.percentage = item.percentage;
                intensity.selected = true;
            });

            const strokeRateIntensitiesPaddling = isPaddlingSession(session.sport.id)
                ? get(session, 'stroke_rate_intensities.data') || []
                : [];
            strokeRateIntensitiesPaddling.forEach(item => {
                const strokeRateIntensityPaddling = details.stroke_rate_intensities_paddling.find(
                    ({ id }) => id === item.id
                );
                if (!strokeRateIntensityPaddling) return;
                strokeRateIntensityPaddling.percentage = item.percentage;
                strokeRateIntensityPaddling.selected = true;
            });

            const strokeRateIntensitiesRowing = isRowingSession(session.sport.id)
                ? get(session, 'stroke_rate_intensities.data') || []
                : [];
            strokeRateIntensitiesRowing.forEach(item => {
                const strokeRateIntensityRowing = details.stroke_rate_intensities_rowing.find(
                    ({ id }) => id === item.id
                );
                if (!strokeRateIntensityRowing) return;
                strokeRateIntensityRowing.percentage = item.percentage;
                strokeRateIntensityRowing.selected = true;
            });

            const heartRateIntensities = get(session, 'heart_rate_intensities.data') || [];
            heartRateIntensities.forEach(item => {
                const heartRateIntensity = details.heart_rate_intensities.find(({ id }) => id === item.id);
                if (!heartRateIntensity) return;
                heartRateIntensity.percentage = item.percentage;
                heartRateIntensity.selected = true;
            });

            const focuses = get(session, 'focuses.data') || [];
            focuses.forEach(item => details.focuses.push(item));

            const tags = get(session, 'tags.data') || [];
            tags.forEach(({ tag_text }) => details.tags.push(tag_text));
        },

        setShowDefaultComponent(state, file) {
            state.showDefaultComponent = file;
        },

        setFromAnotherSessionId(state, sessionId) {
            state.setFromAnotherSessionId = sessionId;
        }
    },
    actions: {
        /**
         * Get session to edit and apply it to createSession module
         * @param {Object} context
         * @param {data} data
         */
        setEditSession(context, data) {
            context.dispatch('emptySession');

            return context
                .dispatch(
                    'sessionDetails/getSession',
                    {
                        id: data,
                        include: [
                            'intensities',
                            'focuses',
                            'participants:fields(user_id|user_role_id|edit|add_all_scores)',
                            'session',
                            'session.structure',
                            'session.structure.athletes:fields(id|full_name)',
                            'tags',
                            'venue'
                        ].join(',')
                    },
                    { root: true }
                )
                .then(response => {
                    context.state.session = response.data;
                    context.dispatch('setSessionDetails');
                    context.dispatch('setSessionParticipants');
                    context.dispatch('setSessionWork');
                })
                .catch(error => {
                    context.dispatch('notifyError', error, { root: true });
                    return error;
                });
        },

        updateSessionTimes(context, { date, duration = 0 }) {
            const session = context.rootState.createSession;

            session.details.startDate = date.toISO();
            session.details.startTime = date.toISO();

            if (duration > 0) {
                session.details.endDate = date.plus({ seconds: duration }).toISO();
                session.details.endTime = date.plus({ seconds: duration }).toISO();
            } else {
                session.details.endDate = date.plus({ hours: 1, minutes: 30 }).toISO();
                session.details.endTime = date.plus({ hours: 1, minutes: 30 }).toISO();
            }
        },

        updateSessionAttendance(context) {
            const session = context.rootState.createSession;

            const athletes = context.rootGetters['calendarQuickAssign/selectedAthletes'];
            const coaches = context.rootGetters['calendarQuickAssign/selectedCoaches'];

            athletes.forEach(athlete => {
                Vue.set(session.attendance.athletes, athlete, 1);
                Vue.set(session.attendance.athlete_permissions, athlete, {
                    edit: 0,
                    add_all_scores: 0
                });
            });

            coaches.forEach(coach => {
                Vue.set(session.attendance.coaches, coach, 1);
                Vue.set(session.attendance.coach_permissions, coach, {
                    edit: 0,
                    add_all_scores: 0
                });
            });
        },

        updateSessionAthletes(context, athletes) {
            athletes.forEach(athleteArg => {
                const athlete = context.rootGetters['clubHelper/clubAthletes'].find(({ id }) => id === athleteArg);
                if (athlete) {
                    Vue.set(context.rootState.createSession.attendance.athletes, athlete.id, 1);
                    Vue.set(context.rootState.createSession.attendance.athlete_permissions, athlete.id, {
                        edit: 0,
                        add_all_scores: 0
                    });
                }
            });
        },

        emptySession(context) {
            const s = context.rootState.createSession;

            s.isFilled = false;

            s.details.name = '';
            s.details.sessionType = null;
            s.details.session_id = null;
            s.details.all_day_event = false;
            s.details.attendance_limit = null;
            s.details.planned_rpe = null;
            s.details.startDate = DateTime.now().toISO();
            s.details.startTime = DateTime.now().set({ hour: 7, minute: 0, second: 0 }).toISO();
            s.details.endDate = DateTime.now().toISO();
            s.details.endTime = DateTime.now().set({ hour: 8, minute: 30, second: 0 }).toISO();
            s.details.totalDistance = '';
            s.details.totalTime = '';
            s.details.target_stroke = null;
            s.details.rolling_start = false;
            s.details.start_sequence = false;
            s.details.location = {};
            s.details.description = '';
            s.details.weather = '';
            s.details.tags = [];
            s.details.focuses = [];
            s.details.favorite = false;

            Object.keys(s.details.repeat).forEach(key => {
                s.details.repeat[key] = null;

                if (key === 'repeat_every') {
                    s.details.repeat[key] = 1;
                }

                if (key === 'repeat_end_date') {
                    s.details.repeat[key] = TODAY;
                }

                if (key === 'repeat_on_the_specific_days') {
                    s.details.repeat[key] = [];
                }
            });

            const intensities = get(s, 'details.intensities') || [];
            intensities.forEach(intensity => {
                intensity.selected = false;
                intensity.percentage = 0;
            });

            const strokeRateIntensitiesPaddling = get(s, 'details.stroke_rate_intensities_paddling') || [];
            strokeRateIntensitiesPaddling.forEach(strokeRateIntensityPaddling => {
                strokeRateIntensityPaddling.selected = false;
                strokeRateIntensityPaddling.percentage = 0;
            });

            const strokeRateIntensitiesRowing = get(s, 'details.stroke_rate_intensities_rowing') || [];
            strokeRateIntensitiesRowing.forEach(strokeRateIntensityRowing => {
                strokeRateIntensityRowing.selected = false;
                strokeRateIntensityRowing.percentage = 0;
            });

            const heartRateIntensities = get(s, 'details.heart_rate_intensities') || [];
            heartRateIntensities.forEach(heartRateIntensity => {
                heartRateIntensity.selected = false;
                heartRateIntensity.percentage = 0;
            });

            s.attendance = {
                creatorId: null,
                athletes: {},
                coaches: {},
                coach_permissions: {},
                athlete_permissions: {}
            };

            const userRoleId = get(context.rootGetters.selectedClub, 'roles.data[0].id');
            const userId = context.rootGetters['clubHelper/me'].id;

            if (isCoach(userRoleId)) {
                Vue.set(s.attendance.coaches, userId, true);
                Vue.set(s.attendance.coach_permissions, userId, {
                    edit: true,
                    add_all_scores: true
                });
            } else if (isAthlete(userRoleId)) {
                Vue.set(s.attendance.athletes, userId, true);
                Vue.set(s.attendance.athlete_permissions, userId, {
                    edit: true,
                    add_all_scores: true
                });
            }
            s.seat_racing = false;
            s.work = {
                createPieces: false,
                repetitions: null,
                pieceTypes: {
                    single: true,
                    multiple: false,
                    variable: false
                },
                pieces: [
                    {
                        distance: null,
                        time: null,
                        split_length: null,
                        variable_split_lengths: [],
                        rest: null,
                        target_stroke: null,
                        rolling_start: false,
                        start_sequence: false
                    }
                ],
                scStructures: []
            };
            return s;
        },

        setSessionDetails(context) {
            const session = context.state.session;
            session.sessionDuration =
                (DateTime.fromSQL(session.end_date) - DateTime.fromSQL(session.start_date)) / 1000;
            const details = context.rootState.createSession.details;
            context.rootState.createSession.isFilled = true;
            context.rootState.createSession.seat_racing = context.state.session.seat_racing;
            return context.commit('updateSession', { session, details });
        },

        setSessionParticipants(context) {
            const session = context.state.session;
            const attendance = context.rootState.createSession.attendance;

            attendance.creatorId = session.creator_id;
            attendance.athletes = {};
            attendance.athlete_permissions = {};
            attendance.coaches = {};
            attendance.coach_permissions = {};

            const participants = get(session, 'participants.data') || [];
            participants.forEach(participant => {
                if (isAthlete(participant.user_role_id)) {
                    Vue.set(context.rootState.createSession.attendance.athletes, participant.user_id, 1);
                    Vue.set(context.rootState.createSession.attendance.athlete_permissions, participant.user_id, {
                        edit: participant.edit ? 1 : 0,
                        add_all_scores: participant.add_all_scores ? 1 : 0
                    });
                }

                if (isCoach(participant.user_role_id)) {
                    Vue.set(context.rootState.createSession.attendance.coaches, participant.user_id, 1);
                    Vue.set(context.rootState.createSession.attendance.coach_permissions, participant.user_id, {
                        edit: participant.edit ? 1 : 0,
                        add_all_scores: participant.add_all_scores ? 1 : 0
                    });
                }
            });
        },

        setSessionWork(context) {
            const structures = get(context, 'state.session.session.data.structure.data') || [];
            structures.forEach(structure => {
                if (isScRelatedSession(structure.sport.id)) {
                    context.dispatch('setSCWorkStructure', structure);
                } else {
                    context.dispatch('setWorkStructure', structure);
                }
            });
        },

        setWorkStructure(context, structure) {
            if (structure.pieces_count > 0) {
                context.rootState.createSession.work.createPieces = true;
                context.rootState.createSession.work.repetitions =
                    structure.pieces_count > 1 ? structure.pieces_count : 2;
            }

            const types = context.rootState.createSession.work.pieceTypes;
            Object.keys(types).forEach(key => {
                types[key] = false;
            });
            switch (structure.piece_structure) {
                case 1:
                    types.single = true;
                    break;
                case 2:
                    types.multiple = true;
                    break;
                case 3:
                    types.variable = true;
                    break;
                default:
                    types.single = true;
                    break;
            }

            if (structure.pieces && structure.pieces.data.length) {
                context.rootState.createSession.work.pieces = [];

                const pieces = get(structure, 'pieces.data') || [];
                pieces.forEach(piece => {
                    const newPiece = {
                        distance: piece.planned_distance > 0 ? piece.planned_distance : null,
                        time: piece.planned_time > 0 ? piece.planned_time : null,
                        split_length: piece.split_length > 0 ? piece.split_length : null,
                        variable_split_lengths: [],
                        rest: piece.rest > 0 ? piece.rest : null,
                        target_stroke: piece.target_stroke != null ? piece.target_stroke : null,
                        variable_target_strokes: [],
                        rolling_start: !!piece.rolling_start,
                        start_sequence: !!piece.start_sequence
                    };

                    if (piece.segments && piece.type_of_split_length === 2) {
                        const segments = get(piece, 'segments.data') || [];
                        segments.forEach(segment => {
                            if (segment.planned_distance) {
                                newPiece.variable_split_lengths.push(segment.planned_distance);
                            } else if (segment.planned_time) {
                                newPiece.variable_split_lengths.push(segment.planned_time);
                            }
                        });
                    }

                    context.rootState.createSession.work.pieces.push(newPiece);
                });

                // For multiple piece type the form models all repetitions as a single
                // base piece (pieces[0]) plus a repetitions count. If the loaded pieces
                // have differing target stroke rates, surface them as variable target
                // strokes; otherwise keep the single shared target stroke.
                if (types.multiple && isWaterSession(structure.sport.id)) {
                    const loadedPieces = context.rootState.createSession.work.pieces;
                    const strokes = loadedPieces.map(({ target_stroke }) =>
                        target_stroke != null ? target_stroke : null
                    );
                    const allSame = strokes.every(s => s === strokes[0]);

                    const base = loadedPieces[0];
                    if (!allSame) {
                        // Build the variable entries BEFORE nulling base.target_stroke —
                        // base IS loadedPieces[0], so reading it after nulling would lose
                        // the first piece's value.
                        const variableStrokes = loadedPieces.map(piece => ({
                            target_stroke: piece.target_stroke != null ? piece.target_stroke : null,
                            rolling_start: !!piece.rolling_start
                        }));
                        base.target_stroke = null;
                        base.variable_target_strokes = variableStrokes;
                    } else {
                        base.variable_target_strokes = [];
                    }

                    // Multiple keeps only the base piece in the form.
                    context.rootState.createSession.work.pieces = [base];
                }

                if (isWaterSession(structure.sport.id) && context.rootState.createSession.work.pieces.length) {
                    const p0 = context.rootState.createSession.work.pieces[0];
                    const details = context.rootState.createSession.details;
                    const hasVariableStrokes = p0.variable_target_strokes && p0.variable_target_strokes.length;
                    if (!hasVariableStrokes && p0.target_stroke == null && details.target_stroke != null) {
                        Vue.set(p0, 'target_stroke', details.target_stroke);
                    }
                    if (!p0.rolling_start && details.rolling_start) {
                        Vue.set(p0, 'rolling_start', true);
                    }
                    if (!p0.start_sequence && details.start_sequence) {
                        Vue.set(p0, 'start_sequence', true);
                    }
                }
            }
        },

        setSCWorkStructure(context, structure) {
            const group = {
                name: structure.name,
                athletes: {},
                exercises: []
            };

            const structureAthletes = get(structure, 'athletes.data') || [];
            structureAthletes.forEach(athlete => {
                Vue.set(group.athletes, athlete.id, 1);
            });

            const pieces = get(structure, 'pieces.data') || [];
            pieces.forEach(piece => {
                const exercise = {
                    exercise_id: piece.exercise_id,
                    sets_count: piece.sets_count
                };

                if (piece.segments) {
                    const segments = get(piece, 'segments.data') || [];
                    const reps = segments.map(({ reps_count }) => reps_count);

                    if (!_.without(reps, reps[0]).length) {
                        // If all reps are equal
                        exercise.reps_count = reps[0];
                        exercise.segments = [];
                    } else {
                        exercise.reps_count = 'custom';
                        exercise.segments = reps;
                    }
                }

                group.exercises.push(exercise);
            });

            context.rootState.createSession.work.scStructures.push(group);
        },

        updateSession(context, payload) {
            return context.dispatch(
                'post',
                {
                    url: api + 'update-session',
                    data: payload
                },
                { root: true }
            );
        },

        updateEvent(context, payload) {
            return context.dispatch(
                'post',
                {
                    url: api + 'update-event',
                    data: payload
                },
                { root: true }
            );
        },

        /**
         * Get favourite or recent session to createSession module
         * @param {Object} context
         * @param {Number} sessionId
         */
        fillSession(context, sessionId) {
            const storedDate = context.rootState.createSession.details.startDate;
            context.dispatch('emptySession');

            return context
                .dispatch(
                    'sessionDetails/getSession',
                    {
                        id: sessionId,
                        include: ['intensities', 'focuses', 'session', 'session.structure', 'tags', 'venue'].join(',')
                    },
                    { root: true }
                )
                .then(response => {
                    context.state.session = response.data;
                    context.dispatch('setSessionDetails');
                    context.dispatch('setSessionWork');
                    const duration =
                        (DateTime.fromSQL(context.state.session.end_date) -
                            DateTime.fromSQL(context.state.session.start_date)) /
                        1000;
                    context.dispatch('updateSessionTimes', {
                        date: DateTime.fromISO(storedDate),
                        duration: duration
                    });
                    context.state.setFromAnotherSessionId &&
                        context.commit('resetRepeatStructure', null, { root: true });
                });
        }
    }
};

export default editSession;
